// Б.2.c: Тёмный нетраннер — passive с условием по линии.
//
// Пока Нетраннер на 2-Й ЛИНИИ владельца, КАЖДЫЙ РАЗ когда любой союзный
// персонаж (юнит ИЛИ Герой) получает урон, Нетраннер наносит 2 урона
// случайному вражескому персонажу.
//
// Анти-рекурсия: одно «событие урона» = одно срабатывание каждого Нетраннера
// (по instanceId). Состояние события держим на state._currentDmgEvent
// {firedNetranners: Set<instanceId>}. Если notifyCharacterDamaged вызывается
// рекурсивно из своего собственного выстрела (или Хирурга, или цепочки) —
// тот же Set предотвращает бесконечный цикл.
//
// 2 урона — обычный, броня поглощает (для юнитов), Иммунитет/Парашют для
// Героя — через applyHeroDamage.
//
// Подключение: notifyCharacterDamaged вызывается из всех точек урона:
//   - onUnitDamaged (deus/index.js — авто-атака, ответка, hero-vs-unit).
//   - onHeroDamaged (deus/index.js — авто-атака по Герою, hero-vs-hero,
//     applyHeroDamage, deck empty, Mod progression).
//   - Vzlom (1 урон Действием).
//   - Eksp. батарея (5 ударов).
//   - Сам Тёмный нетраннер при выстреле (через applyDamage/applyHeroDamage).

import { applyDamage } from '../../combat.js';
import { applyHeroDamage } from '../../hero-damage.js';
import { dispatchUnitDied } from '../deus/hooks.js';
import { shortHeroName } from '../../../data/heroes.js';

// Этап 56-fix: id ИСПРАВЛЕН — реальный id карты в v6 = 'syn_temny_netrunner'
// (раньше тут стоял 'syn_temnyy_netranner' → пассивка НИКОГДА не срабатывала,
// т.к. fireNetrannersOnSide искала несуществующий id).
export const CARD_ID = 'syn_temny_netrunner';

// Главная точка входа. Вызывается из всех мест нанесения урона.
//   victimOwnerIdx — на чьей стороне находится получивший урон персонаж.
//   victimCard     — Card, получивший урон (юнит или Герой).
//   dmg            — фактический урон по HP+броне (taken).
export function notifyCharacterDamaged(state, victimOwnerIdx, victimCard, dmg) {
  if (!victimCard || dmg <= 0) return;
  const isTopLevel = !state._currentDmgEvent;
  if (isTopLevel) {
    state._currentDmgEvent = { firedNetranners: new Set() };
  }
  try {
    fireNetrannersOnSide(state, victimOwnerIdx);
  } finally {
    if (isTopLevel) state._currentDmgEvent = null;
  }
}

function fireNetrannersOnSide(state, victimOwnerIdx) {
  const p = state.players[victimOwnerIdx];
  // Снимок: ВСЕ живые Нетраннеры на ОБЕИХ линиях (привязка к 2-й линии убрана —
  // выбор фаундера). Порядок: 1-я линия, затем 2-я, слева-направо.
  const netrans = [];
  for (let line = 0; line < 2; line++) {
    for (let slot = 0; slot < 4; slot++) {
      const u = p.field[line][slot];
      if (u && u.isAlive() && u.id === CARD_ID) netrans.push(u);
    }
  }
  if (netrans.length === 0) return;
  const fired = state._currentDmgEvent.firedNetranners;
  for (const nt of netrans) {
    if (fired.has(nt.instanceId)) continue;
    fired.add(nt.instanceId);
    netrannerShoot(state, victimOwnerIdx, nt);
  }
}

function netrannerShoot(state, ownerIdx, netranner) {
  if (!netranner.isAlive()) return;
  const enemyIdx = 1 - ownerIdx;
  const enemy = state.players[enemyIdx];
  const ownerName = state.players[ownerIdx].name;

  // Кандидаты: вражеские юниты + вражеский Герой.
  const candidates = [];
  for (const u of enemy.unitsOnField()) candidates.push(u);
  if (enemy.hero.hp > 0) candidates.push(enemy.hero);
  if (candidates.length === 0) {
    state.log.add(`📡 Тёмный нетраннер [${ownerName}]: цель не найдена, выстрел пропадает`);
    return;
  }
  const target = candidates[state.rng.int(candidates.length)];

  if (target.type === 'hero') {
    state.log.add(`📡 Тёмный нетраннер [${ownerName}] → ${shortHeroName(target)} [${enemy.name}]: 2 урона`);
    const r = applyHeroDamage(state, enemyIdx, 2, { pierce: false });
    const taken = (r.armorAbsorbed || 0) + (r.hpLost || 0);
    if (taken > 0) notifyCharacterDamaged(state, enemyIdx, target, taken);
  } else {
    const before = { hp: target.hp, armor: target.armor };
    const r = applyDamage(target, 2, { pierce: false });
    const taken = (r.armorAbsorbed || 0) + (r.hpLost || 0);
    state.log.add(
      `📡 Тёмный нетраннер [${ownerName}] → ${target.name_ru} [${enemy.name}]: 2 урона ` +
      `(HP ${before.hp}→${target.hp}, броня ${before.armor}→${target.armor})`
    );
    if (taken > 0) notifyCharacterDamaged(state, enemyIdx, target, taken);
    if (target.hp <= 0) {
      enemy.removeFromField(target);
      state.log.add(`✗ ${target.name_ru} [${enemy.name}] погибает`);
      dispatchUnitDied(state, {
        ownerIdx: enemyIdx, card: target,
        killer: netranner, killerIdx: ownerIdx, killerWasCounter: false
      });
    }
  }
}
